import { Shield, AlertTriangle, CheckCircle2, Radio, MapPin, Clock } from "lucide-react";
import { Button, message, Popconfirm } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ILoneWorkerSession, ILoneSosEvent } from "../../../types/api/payslips";
import { useLoneWorkerSessions, useLoneWorkerSosList, useLoneWorkerSosResolve } from "../../../hooks/api/payslips/hooks";

dayjs.extend(relativeTime);

const SummaryCard = ({ icon, iconBg, value, label, sub }: { icon: React.ReactNode; iconBg: string; value: string | number; label: string; sub?: string }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>{icon}</div>
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">{label}</div>
      <div className="text-2xl font-bold text-slate-800 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const SESSION_STATUS: Record<string, { dot: string; cls: string; label: string }> = {
  active: { dot: "bg-emerald-500", cls: "text-emerald-700", label: "Active" },
  completed: { dot: "bg-slate-300", cls: "text-slate-500", label: "Completed" },
  sos: { dot: "bg-red-500", cls: "text-red-600", label: "SOS" },
  missed_checkin: { dot: "bg-amber-400", cls: "text-amber-700", label: "Missed Check-in" },
  resolved: { dot: "bg-blue-300", cls: "text-blue-600", label: "Resolved" },
};

const LoneWorkerPage = () => {
  const [msgApi, ctx] = message.useMessage();

  const { data: sessRes } = useLoneWorkerSessions({ page: 1, limit: 50 });
  const sessions: ILoneWorkerSession[] = (sessRes?.data as any) ?? [];

  const { data: sosRes, refetchWithQuery: refetchSos } = useLoneWorkerSosList({ resolved: "false" });
  const sosEvents: ILoneSosEvent[] = (sosRes?.data as any) ?? [];

  const resolveSos = useLoneWorkerSosResolve({
    onSuccess: () => { msgApi.success("SOS resolved."); refetchSos({}); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const active = sessions.filter((s) => s.status === "active").length;
  const unresolvedSos = sosEvents.filter((e) => !e.resolved_at).length;
  const missed = sessions.filter((s) => s.status === "missed_checkin").length;

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Lone Worker</h1>
          <p className="text-slate-400 text-sm">Monitor field worker safety sessions and SOS alerts</p>
        </div>
        {active > 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-semibold text-emerald-700">{active} Active Session{active !== 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon={<Radio size={18} className="text-emerald-500" />} iconBg="bg-emerald-50" value={active} label="Active Sessions" sub="monitoring live" />
        <SummaryCard icon={<Shield size={18} className="text-blue-500" />} iconBg="bg-blue-50" value={sessions.length} label="Total Sessions" sub="all time" />
        <SummaryCard icon={<AlertTriangle size={18} className="text-amber-500" />} iconBg="bg-amber-50" value={missed} label="Missed Check-ins" sub="active sessions" />
        <SummaryCard icon={<CheckCircle2 size={18} className={unresolvedSos > 0 ? "text-red-500" : "text-slate-400"} />} iconBg={unresolvedSos > 0 ? "bg-red-50" : "bg-slate-50"} value={unresolvedSos} label="Unresolved SOS" sub={unresolvedSos > 0 ? "action required" : "all clear"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* SESSIONS */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
            <span className="text-sm font-semibold text-slate-700">Worker Sessions</span>
          </div>
          {sessions.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-400 text-sm">No sessions found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide border-b border-slate-100">
                  <th className="text-left px-5 py-3">Worker</th>
                  <th className="text-left py-3">Job</th>
                  <th className="text-left py-3">Interval</th>
                  <th className="text-left py-3">Started</th>
                  <th className="text-left py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const cfg = SESSION_STATUS[s.status] ?? SESSION_STATUS.completed;
                  return (
                    <tr key={s.id} className="border-t border-slate-50 hover:bg-slate-50/70 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.status === "active" ? "animate-pulse " : ""}${cfg.dot}`} />
                          <div>
                            <div className="font-medium text-slate-800 text-sm">{s.worker_name || "—"}</div>
                            <div className="text-[10px] text-slate-400">{s.status.toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="text-xs font-mono text-primary">{s.job_number || "—"}</div>
                      </td>
                      <td className="py-3 text-slate-600 text-sm">
                        <div className="flex items-center gap-1"><Clock size={12} className="text-slate-400" /> {s.check_interval_mins} min</div>
                      </td>
                      <td className="py-3 text-slate-500 text-xs">{dayjs(s.start_time).format("DD MMM HH:mm")}</td>
                      <td className="py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cfg.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* SOS ALERTS */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">SOS Alerts</span>
            {unresolvedSos > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unresolvedSos} active</span>
            )}
          </div>
          {sosEvents.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-400 text-sm">
              <Shield size={32} className="mx-auto mb-2 text-emerald-200" />
              No unresolved SOS alerts
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {sosEvents.map((a) => (
                <div key={a.id} className="px-5 py-4 bg-red-50/40">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle size={13} className="text-red-500" />
                      <span className="text-xs font-semibold text-red-600">SOS Alert</span>
                    </div>
                    <Popconfirm
                      title="Resolve this SOS alert?"
                      onConfirm={() => resolveSos.mutate({ id: a.id })}
                      okText="Resolve"
                    >
                      <Button size="small" danger type="default" className="text-[10px] h-5">Resolve</Button>
                    </Popconfirm>
                  </div>
                  <div className="text-sm font-medium text-slate-800">{a.worker_name || "—"}</div>
                  {(a.lat || a.lng) && (
                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                      <MapPin size={11} />{a.lat?.toFixed(4)}, {a.lng?.toFixed(4)}
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-0.5">Triggered: {dayjs(a.triggered_at).fromNow()}</div>
                  {a.emergency_services_called ? (
                    <div className="text-xs font-semibold text-red-600 mt-1">Emergency services called</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoneWorkerPage;
